import { useCallback, useDeferredValue, useMemo, useState } from "react"

import { LayoutDashboard, Search } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { DashboardSpec } from "../dashboard/catalog"
import type { SavedDashboard } from "../services/dashboard-service"

import { SidebarNavSection } from "@/components/sidebar/sidebar-nav-section"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import { useSidebar } from "@/components/ui/use-sidebar"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { cn } from "@/lib/utils"
import { useDashStore } from "@/stores/dash-store"

import { CollapsedDashboardPopover } from "./collapsed-dashboard-popover"
import { DashboardCollapsibleSection } from "./dashboard-collapsible-section"
import { DashboardListItem } from "./dashboard-list-item"
import {
  useDashboards,
  useDeleteDashboard,
  useToggleDashboardFavorite,
} from "../hooks/use-dashboards"
import {
  getDashStorageKey,
  takeVisibleByPriority,
  trackRecentlyViewed,
} from "../utils/dashboard-helpers"

const DEFAULT_VISIBLE_COUNT = 5

const FAVORITES_SECTION_OPEN_KEY_BASE = "sidebar-dashboards-favorites-open"
const COMPANY_SECTION_OPEN_KEY_BASE = "sidebar-dashboards-company-open"
const PERSONAL_SECTION_OPEN_KEY_BASE = "sidebar-dashboards-personal-open"

type DashboardsFolderProps = {
  className?: string
  defaultExpanded?: boolean
  globalSearchTerm?: string
}

export function DashboardsFolder({
  className,
  defaultExpanded = true,
  globalSearchTerm = "",
}: DashboardsFolderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsedSidebar = sidebarState === "collapsed" && !isMobile

  // Always use "dash" session type so the store instance matches what DashPage reads,
  // regardless of which sidebar (Explorer vs Dash) renders this folder.
  const explorerStore = useDashStore("dash")
  const loadDashboard = explorerStore((s) => s.loadDashboard)
  const setDashboardMode = explorerStore((s) => s.setDashboardMode)

  const { data: dashboardsData, isPending: isLoadingDashboards } = useDashboards()
  const dashboards = dashboardsData ?? []
  const deleteDashboardMutation = useDeleteDashboard()
  const toggleFavoriteMutation = useToggleDashboardFavorite()

  const [localSearchTerm, setLocalSearchTerm] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showAllItems, setShowAllItems] = useState(false)

  const [dashboardToDelete, setDashboardToDelete] = useState<SavedDashboard | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const effectiveSearchTerm = useDeferredValue(globalSearchTerm || localSearchTerm)
  const isSearching = Boolean(effectiveSearchTerm)
  const favoritesSectionOpenKey = useMemo(
    () => getDashStorageKey(FAVORITES_SECTION_OPEN_KEY_BASE),
    []
  )
  const companySectionOpenKey = useMemo(() => getDashStorageKey(COMPANY_SECTION_OPEN_KEY_BASE), [])
  const personalSectionOpenKey = useMemo(
    () => getDashStorageKey(PERSONAL_SECTION_OPEN_KEY_BASE),
    []
  )

  // localStorage-persisted section state
  const [isFavoritesSectionOpenStored, setIsFavoritesSectionOpen] = usePersistedState(
    favoritesSectionOpenKey,
    true
  )
  const [isCompanySectionOpenStored, setIsCompanySectionOpen] = usePersistedState(
    companySectionOpenKey,
    true
  )
  const [isPersonalSectionOpenStored, setIsPersonalSectionOpen] = usePersistedState(
    personalSectionOpenKey,
    true
  )

  const isFavoritesSectionOpen = isSearching ? true : isFavoritesSectionOpenStored
  const isCompanySectionOpen = isSearching ? true : isCompanySectionOpenStored
  const isPersonalSectionOpen = isSearching ? true : isPersonalSectionOpenStored

  // Filtered and categorized dashboards
  const filteredDashboards = useMemo(() => {
    if (!effectiveSearchTerm) return dashboards
    return dashboards.filter(
      (d) =>
        d.title.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        d.description.toLowerCase().includes(effectiveSearchTerm.toLowerCase())
    )
  }, [dashboards, effectiveSearchTerm])

  const favoriteDashboards = useMemo(
    () => filteredDashboards.filter((d) => d.is_favorite),
    [filteredDashboards]
  )
  const nonFavoriteDashboards = useMemo(
    () => filteredDashboards.filter((d) => !d.is_favorite),
    [filteredDashboards]
  )
  const companyDashboards = useMemo(
    () => nonFavoriteDashboards.filter((d) => d.type === "company"),
    [nonFavoriteDashboards]
  )
  const personalDashboards = useMemo(
    () => nonFavoriteDashboards.filter((d) => d.type === "personal"),
    [nonFavoriteDashboards]
  )

  // Visible items (limited or all)
  const { visibleFavorites, visibleCompany, visiblePersonal } = useMemo(() => {
    if (showAllItems || effectiveSearchTerm) {
      return {
        visibleFavorites: favoriteDashboards,
        visibleCompany: companyDashboards,
        visiblePersonal: personalDashboards,
      }
    }
    const visible = takeVisibleByPriority(DEFAULT_VISIBLE_COUNT, {
      favorites: favoriteDashboards,
      company: companyDashboards,
      personal: personalDashboards,
    })
    return {
      visibleFavorites: visible.favorites,
      visibleCompany: visible.company,
      visiblePersonal: visible.personal,
    }
  }, [favoriteDashboards, companyDashboards, personalDashboards, showAllItems, effectiveSearchTerm])

  // Handlers
  const handleDashboardClick = useCallback(
    (dashboard: SavedDashboard) => {
      trackRecentlyViewed(dashboard.id)
      // query_bindings live on the doc envelope, not inside the stored spec
      // JSON (backend strips them on save so the persisted spec is canonical).
      // Merge them back onto the runtime spec so BindingsProvider sees them
      // — without this every tile in a bound dashboard rendered the amber
      // "AI refresh" sparkle instead of the green "Live SQL" database icon.
      const storedSpec = dashboard.spec as DashboardSpec
      const runtimeSpec: DashboardSpec = {
        ...storedSpec,
        queryBindings: dashboard.query_bindings ?? storedSpec.queryBindings,
      }
      loadDashboard(dashboard.id, runtimeSpec, {
        updateOnLoad: dashboard.update_on_load,
        dashboardType: dashboard.type,
        title: dashboard.title,
        qualityReview: dashboard.quality_review ?? null,
        qualityHistory: dashboard.quality_history ?? [],
      })
      if (!window.location.pathname.startsWith("/dash")) {
        void navigate("/dash")
      }
    },
    [loadDashboard, navigate]
  )

  const handleToggleFavorite = useCallback(
    (e: React.MouseEvent, dashboardId: string) => {
      e.stopPropagation()
      toggleFavoriteMutation.mutate(dashboardId)
    },
    [toggleFavoriteMutation]
  )

  const handleConfirmDelete = async () => {
    if (!dashboardToDelete) return
    setIsDeleting(true)
    try {
      await deleteDashboardMutation.mutateAsync({
        dashboardId: dashboardToDelete.id,
        type: dashboardToDelete.type,
      })
      toast.success(t("dash.dashboard.deleted"))
    } catch {
      toast.error(t("dash.dashboard.delete_error"))
    } finally {
      setIsDeleting(false)
      setDashboardToDelete(null)
    }
  }

  const handleHeaderClick = useCallback(() => {
    setDashboardMode("landing")
    void navigate("/dash")
  }, [navigate, setDashboardMode])

  const headerActions = (
    <>
      {!globalSearchTerm && (
        <Button
          aria-label={t("dash.landing.sidebar.search")}
          size="icon"
          variant="ghost"
          className={cn(
            "hover:bg-foreground/15 size-7 rounded-full p-0",
            localSearchTerm && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onClick={(e) => {
            e.stopPropagation()
            setIsSearchOpen(true)
          }}
        >
          <Search className="size-4" />
        </Button>
      )}
    </>
  )

  const deleteDialog = (
    <Dialog open={!!dashboardToDelete} onOpenChange={(open) => !open && setDashboardToDelete(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dash.landing.delete")}</DialogTitle>
          <DialogDescription>
            {dashboardToDelete?.title
              ? t("dash.landing.confirm_delete_named", { name: dashboardToDelete.title })
              : t("dash.landing.confirm_delete")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDashboardToDelete(null)}>
            {t("common.cancel")}
          </Button>
          <Button disabled={isDeleting} variant="destructive" onClick={handleConfirmDelete}>
            {t("dash.landing.delete")}
            {isDeleting && <Spinner className="ml-2" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // ─── Collapsed sidebar: Popover ─────────────────────
  if (isCollapsedSidebar) {
    return (
      <CollapsedDashboardPopover
        className={className}
        companyDashboards={companyDashboards}
        dashboards={dashboards}
        deleteDialog={deleteDialog}
        effectiveSearchTerm={localSearchTerm}
        favoriteDashboards={favoriteDashboards}
        filteredDashboards={filteredDashboards}
        isCompanySectionOpen={isCompanySectionOpen}
        isFavoritesSectionOpen={isFavoritesSectionOpen}
        isLoadingDashboards={isLoadingDashboards}
        isPersonalSectionOpen={isPersonalSectionOpen}
        isSearching={isSearching}
        personalDashboards={personalDashboards}
        setIsCompanySectionOpen={setIsCompanySectionOpen}
        setIsFavoritesSectionOpen={setIsFavoritesSectionOpen}
        setIsPersonalSectionOpen={setIsPersonalSectionOpen}
        onDashboardClick={handleDashboardClick}
        onDelete={setDashboardToDelete}
        onSearchChange={setLocalSearchTerm}
        onToggleFavorite={handleToggleFavorite}
      />
    )
  }

  // ─── Expanded sidebar ───────────────────────────────
  return (
    <>
      <SidebarNavSection
        allItemsShown={showAllItems}
        className={className}
        defaultExpanded={defaultExpanded}
        forceExpanded={!!globalSearchTerm}
        headerActions={headerActions}
        icon={<LayoutDashboard className="size-4" />}
        isSearchActive={isSearchOpen && !globalSearchTerm}
        searchPlaceholder={t("dash.landing.sidebar.search")}
        searchTerm={localSearchTerm}
        storageKey={getDashStorageKey("sidebar-dashboards-collapsed")}
        title={t("dash.landing.sidebar.title")}
        totalCount={filteredDashboards.length}
        visibleCount={DEFAULT_VISIBLE_COUNT}
        onHeaderClick={handleHeaderClick}
        onSearchChange={setLocalSearchTerm}
        onSearchClose={() => setIsSearchOpen(false)}
        onSeeAllClick={() => setShowAllItems(true)}
        onShowLessClick={() => setShowAllItems(false)}
      >
        {isLoadingDashboards && (
          <p className="animate-shimmer from-muted-foreground via-foreground/50 to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
            {t("dash.landing.sidebar.empty")}
          </p>
        )}
        {!isLoadingDashboards &&
          visibleFavorites.length === 0 &&
          visibleCompany.length === 0 &&
          visiblePersonal.length === 0 && (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              {effectiveSearchTerm
                ? t("dash.landing.sidebar.no_matches")
                : t("dash.landing.sidebar.empty")}
            </p>
          )}

        {(visibleFavorites.length > 0 ||
          visibleCompany.length > 0 ||
          visiblePersonal.length > 0) && (
          <div className="flex flex-col gap-4">
            <DashboardCollapsibleSection
              isOpen={isFavoritesSectionOpen}
              isSearching={isSearching}
              items={visibleFavorites}
              label={t("dash.landing.favorites")}
              setOpen={setIsFavoritesSectionOpen}
              renderItem={(d) => (
                <DashboardListItem
                  key={d.id}
                  dashboard={d}
                  variant="expanded"
                  onClick={() => handleDashboardClick(d)}
                  onDelete={setDashboardToDelete}
                  onToggleFavorite={handleToggleFavorite}
                />
              )}
            />
            <DashboardCollapsibleSection
              isOpen={isCompanySectionOpen}
              isSearching={isSearching}
              items={visibleCompany}
              label={t("dash.landing.company_badge")}
              setOpen={setIsCompanySectionOpen}
              renderItem={(d) => (
                <DashboardListItem
                  key={d.id}
                  dashboard={d}
                  variant="expanded"
                  onClick={() => handleDashboardClick(d)}
                  onDelete={setDashboardToDelete}
                  onToggleFavorite={handleToggleFavorite}
                />
              )}
            />
            <DashboardCollapsibleSection
              isOpen={isPersonalSectionOpen}
              isSearching={isSearching}
              items={visiblePersonal}
              label={t("dash.landing.personal_badge")}
              setOpen={setIsPersonalSectionOpen}
              renderItem={(d) => (
                <DashboardListItem
                  key={d.id}
                  dashboard={d}
                  variant="expanded"
                  onClick={() => handleDashboardClick(d)}
                  onDelete={setDashboardToDelete}
                  onToggleFavorite={handleToggleFavorite}
                />
              )}
            />
          </div>
        )}
      </SidebarNavSection>
      {deleteDialog}
    </>
  )
}
